import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ArrowRight, Car, Sparkles, MapPin, Gift, Check } from "lucide-react";
import Header from "@/components/Header";

const HERO_IMG =
  "https://images.unsplash.com/photo-1605437241278-c1806d14a4d9?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80";
const BASIS_IMG =
  "https://images.unsplash.com/photo-1471174617910-3e9c04f58ff5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";
const PREMIUM_IMG =
  "https://images.unsplash.com/photo-1629820408206-e9fc918abf63?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Landing() {
  const [packages, setPackages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const imgFor = (name) => (name?.toLowerCase().includes("premium") ? PREMIUM_IMG : BASIS_IMG);

  return (
    <div className="min-h-screen grain">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-background" />
        <div className="absolute inset-0 hero-grid" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-24 pb-32 sm:pt-32 sm:pb-44">
          <div className="max-w-3xl">
            <div className="animate-fade-up inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs uppercase tracking-[0.2em] text-zinc-400 mb-8">
              <Sparkles className="w-3 h-3 text-primary" /> Mobile Fahrzeugaufbereitung
            </div>
            <h1 className="animate-fade-up delay-1 font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
              Innenraum.<br />
              <span className="text-gradient-gold">Wie neu.</span> Vor Ihrer Tür.
            </h1>
            <p className="animate-fade-up delay-2 mt-8 text-lg text-zinc-400 max-w-xl leading-relaxed">
              Professionelle Innenraumaufbereitung — ich komme zu dir. Jede 5. Aufbereitung ist
              <span className="text-primary"> kostenlos</span>.
            </p>
            <div className="animate-fade-up delay-3 mt-10 flex flex-wrap gap-3">
              <Button
                data-testid="hero-cta-book"
                size="lg"
                onClick={() => navigate("/booking")}
                className="rounded-full bg-primary text-primary-foreground hover:brightness-110 px-8 h-12 text-base"
              >
                Termin buchen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                data-testid="hero-cta-packages"
                onClick={() => document.getElementById("packages")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full border-white/15 bg-transparent hover:bg-white/5 px-8 h-12 text-base"
              >
                Pakete ansehen
              </Button>
            </div>

            <div className="animate-fade-up delay-4 mt-16 grid grid-cols-3 gap-6 max-w-lg">
              <Stat icon={<Car className="w-4 h-4" />} label="Mobil" value="Vor Ort" />
              <Stat icon={<Gift className="w-4 h-4" />} label="Bonus" value="5+1 Karte" />
              <Stat icon={<MapPin className="w-4 h-4" />} label="Region" value="Deutschland" />
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Pakete</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight">
              Wähle dein <span className="text-gradient-gold">Glanz-Level</span>
            </h2>
          </div>
        </div>
        <div className="mb-10 max-w-2xl text-sm text-zinc-400">
          Die angegebenen Preise sind <span className="text-amber-300">Richtpreise</span>. Bei stärkerer
          Verschmutzung oder besonderem Zeitaufwand kann der Preis nach Aufwand angepasst werden — du
          erfährst es immer <span className="text-white">vor</span> der Aufbereitung.
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {packages.map((p, i) => (
            <div
              key={p.package_id}
              data-testid={`package-card-${i}`}
              className="group relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-950 hover:border-primary/50 transition-all"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={imgFor(p.name)}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-heading text-2xl">{p.name}</h3>
                  <div className="text-3xl font-heading text-gradient-gold font-medium">
                    {p.price.toFixed(0)}€
                  </div>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{p.description}</p>
                <Button
                  data-testid={`package-book-${i}`}
                  onClick={() => navigate("/booking", { state: { packageId: p.package_id } })}
                  className="rounded-full bg-white text-black hover:bg-zinc-200 w-full"
                >
                  Jetzt buchen
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Loyalty section */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Treueprogramm</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-6">
              5 Aufbereitungen, <br />die 6. <span className="text-gradient-gold">geht aufs Haus</span>.
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mb-8 max-w-md">
              Mit jeder abgeschlossenen Innenraumaufbereitung sammelst du einen Stempel auf deiner digitalen
              VIP-Karte. Nach fünf Stempeln schenken wir dir die nächste Aufbereitung.
            </p>
            <ul className="space-y-3 text-sm text-zinc-300">
              {["Digitale Stempelkarte im Konto", "Automatisches Tracking nach jedem Termin", "Gratis-Aufbereitung beim 6. Besuch"].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <Button
              data-testid="loyalty-cta"
              onClick={() => navigate("/register")}
              className="mt-10 rounded-full bg-primary text-primary-foreground hover:brightness-110"
            >
              Konto erstellen & sammeln
            </Button>
          </div>

          <div className="relative">
            <DemoCard />
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 mt-10 py-10 text-center text-xs text-zinc-500">
        © {new Date().getFullYear()} Mo's Glanzwerk · Mobile Fahrzeugaufbereitung
      </footer>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-primary mb-1">{icon}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-heading text-base">{value}</div>
    </div>
  );
}

function DemoCard() {
  return (
    <div className="relative w-full aspect-[1.6/1] rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border border-white/10 p-6 flex flex-col justify-between overflow-hidden shadow-2xl gold-glow">
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-primary/20 rounded-full blur-3xl" />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">VIP Treuekarte</div>
          <div className="font-heading text-xl">Mo's <span className="text-gradient-gold">Glanzwerk</span></div>
        </div>
        <Sparkles className="w-6 h-6 text-primary" />
      </div>
      <div className="relative flex items-center justify-between gap-2 mt-4">
        {[true, true, true, false, false].map((f, i) => (
          <div
            key={i}
            className={
              f
                ? "w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center shadow-[0_0_20px_rgba(245,200,80,0.4)] text-black"
                : "w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-dashed border-zinc-700 flex items-center justify-center text-zinc-600"
            }
          >
            {f ? <Check className="w-5 h-5" strokeWidth={3} /> : <span className="text-xs">{i + 1}</span>}
          </div>
        ))}
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-primary/60 bg-primary/10 flex items-center justify-center text-primary">
          <Gift className="w-6 h-6" />
        </div>
      </div>
      <div className="relative text-xs text-zinc-500">3/5 bis zur Gratis-Aufbereitung</div>
    </div>
  );
}
